import theme from 'config/theme'
import { useFlags } from 'config/useFlagsContext'
import { useProntuarioFamiliarAcompVulnerabilidadeQuery } from 'graphql/hooks.generated'
import { useMemo } from 'react'
import { isUndefinedOrNull } from 'util/checks'

import { AcompanhamentoVulnerabilidadeContent } from '../../soap/aside/acompanhamento-vulnerabilidade/AcompanhamentoVulnerabilidadeContent'
import { ClassificacaoVulnerabilidadeSection } from '../../soap/aside/acompanhamento-vulnerabilidade/ClassificacaoVulnerabilidadeSection'
import { isCidadaoVulneravelAlimentar } from '../../soap/aside/acompanhamento-vulnerabilidade/util-acompanhamentoVulnerabilidade'
import { CONTEXTO_FAMILIA_BOLSA_FAMILIA_TOOLTIP_TEXT } from '../model-prontuarioFamiliar'

interface FamiliaAcompanhamentoVulnerabilidadeSectionProps {
  cidadaoIds: ID[]
  familiaId: ID
}

export function FamiliaAcompanhamentoVulnerabilidadeSection({
  cidadaoIds,
  familiaId,
}: Readonly<FamiliaAcompanhamentoVulnerabilidadeSectionProps>) {
  const { PILOTO_VULNERABILIDADE_FAMILIAR_ENABLED } = useFlags()

  const { data, loading } = useProntuarioFamiliarAcompVulnerabilidadeQuery({
    variables: {
      input: { idCidadaoList: cidadaoIds },
    },
    context: { newRequest: true },
    skip: cidadaoIds.isEmpty(),
  })

  const cidadaos = data?.cidadaos

  const familiarVulneravel = useMemo(
    () => cidadaos?.content?.find((familiar) => isCidadaoVulneravelAlimentar(familiar)),
    [cidadaos]
  )

  const familiaAlvo = useMemo(() => {
    const idFamilia = familiaId.toString()
    return cidadaos?.content
      ?.flatMap((cidadao) => cidadao?.familias ?? [])
      ?.find((familia) => familia.id === idFamilia && !familia?.imovel?.possuiInconsistencia)
  }, [cidadaos, familiaId])

  const vulnerabilidadeData = familiaAlvo?.vulnerabilidadeFamiliar

  const hasBeneficiarioBolsaFamilia = cidadaos?.content?.some(
    (cidadao) => cidadao?.beneficiarioBolsaFamilia?.isBeneficiario
  )

  const hasVulnerabilidadeFamiliar = !isUndefinedOrNull(vulnerabilidadeData)
  const hasBolsaFamiliaOuRiscoAlimentar = hasBeneficiarioBolsaFamilia || !isUndefinedOrNull(familiarVulneravel)
  const hasEscoreVulnerabilidade = PILOTO_VULNERABILIDADE_FAMILIAR_ENABLED && hasVulnerabilidadeFamiliar
  const showAcompanhamentoVulnerabilidade = hasBolsaFamiliaOuRiscoAlimentar || hasEscoreVulnerabilidade

  if (loading) {
    return null
  }

  if (PILOTO_VULNERABILIDADE_FAMILIAR_ENABLED && !showAcompanhamentoVulnerabilidade) {
    return null
  }

  if (PILOTO_VULNERABILIDADE_FAMILIAR_ENABLED) {
    return (
      <ClassificacaoVulnerabilidadeSection
        isBeneficiarioBolsaFamilia={hasBeneficiarioBolsaFamilia}
        dataRegistroInsegurancaAlimentar={familiarVulneravel?.informacoesSocioEconomicasDW?.dataRegistro}
        vulnerabilidadeFamiliar={vulnerabilidadeData}
        bolsaFamiliaTooltipText={CONTEXTO_FAMILIA_BOLSA_FAMILIA_TOOLTIP_TEXT}
      />
    )
  }

  return (
    <AcompanhamentoVulnerabilidadeContent
      loading={loading}
      showAcompanhamentoVulnerabilidade={showAcompanhamentoVulnerabilidade}
      isBeneficiarioBolsaFamilia={hasBeneficiarioBolsaFamilia}
      dataRegistro={familiarVulneravel?.informacoesSocioEconomicasDW?.dataRegistro}
      bolsaFamiliaTooltipText={CONTEXTO_FAMILIA_BOLSA_FAMILIA_TOOLTIP_TEXT}
      titleColor={theme.pallete.text.main}
    />
  )
}
